(() => {
  angular
    .module('finance')
    .controller('finance.views.statementOfAccount', StatementOfAccountPageController);

  StatementOfAccountPageController.$inject = [
    '$filter',
    'moment',
    'appSession',
    'abp.services.app.statement',
    'Hms.ErrorCodes.ErrorCode',
  ];

  // The "All entities" option's value: ng-options reads null as the empty option.

  const allEntities = 'all';

  // The four ageing buckets, in order, with the colour token each one takes (HM-11379 AC1).

  const ageingBuckets = [
    { key: 'current', labelKey: 'StatementOfAccountCurrent', color: 'var(--soa-success)' },
    {
      key: 'overdueUpTo30Days',
      labelKey: 'StatementOfAccountOverdueUpTo30Days',
      color: 'var(--soa-warning)',
    },
    {
      key: 'overdueUpTo60Days',
      labelKey: 'StatementOfAccountOverdueUpTo60Days',
      color: 'var(--soa-chart-5)',
    },
    {
      key: 'overdueOver60Days',
      labelKey: 'StatementOfAccountOverdueOver60Days',
      color: 'var(--soa-destructive)',
    },
  ];

  function StatementOfAccountPageController($filter, moment, appSession, statementSvc, errorCodes) {
    const vm = this;

    // The codes StatementAppService returns when it refuses the statement.

    const notAvailableErrorCode = errorCodes.StatementOfAccountNotAvailable.id;
    const restrictedErrorCode = errorCodes.StatementOfAccountRestricted.id;

    vm.isLoadingContext = true;
    vm.isRestricted = false;
    vm.isPreparing = false;
    vm.context = null;
    vm.alert = null;

    vm.isLoadingSummary = false;
    vm.summary = null;
    vm.loadFailure = null;
    vm.entityOptions = [];
    vm.selectedCode = null;
    vm.view = null;

    vm.canDownload = canDownload;
    vm.download = download;
    vm.headingDate = headingDate;
    vm.loadSummary = loadSummary;
    vm.selectEntity = selectEntity;
    vm.formatMoney = formatMoney;

    init();

    function init() {
      statementSvc
        .getStatementOfAccountContext({ abpHandleError: false })
        .success((data) => {
          vm.context = data;
          loadSummary();
        })
        .error((error) => {
          if (error && error.code === restrictedErrorCode) {
            vm.isRestricted = true;
            return;
          }

          vm.alert = {
            type: 'danger',
            title: App.localize('StatementOfAccountDownloadFailedTitle'),
            message: getErrorMessage(error),
          };
        })
        .finally(() => {
          vm.isLoadingContext = false;
        });
    }

    // The figures arrive with every entity's breakdown in one response, so switching is instant.

    function loadSummary() {
      vm.isLoadingSummary = true;
      vm.loadFailure = null;

      statementSvc
        .getStatementSummary({ abpHandleError: false })
        .success((data) => {
          vm.summary = data;
          vm.entityOptions = getEntityOptions(data.entities);
          vm.selectedCode = vm.entityOptions.length ? vm.entityOptions[0].code : null;
          selectEntity();
        })
        .error((error) => {
          if (error && error.code === restrictedErrorCode) {
            vm.isRestricted = true;
            return;
          }

          vm.loadFailure = {
            title: App.localize('StatementOfAccountLoadFailedTitle'),
            message: getErrorMessage(error),
          };
        })
        .finally(() => {
          vm.isLoadingSummary = false;
        });
    }

    // AC2: "All entities (n)" first when there are several; a single entity is offered on its own.

    function getEntityOptions(entities) {
      const options = entities.map((x) => ({ code: x.billingAccountCode, label: x.label }));
      if (options.length <= 1) return options;

      return [
        { code: allEntities, label: App.localize('StatementOfAccountAllEntities', options.length) },
      ].concat(options);
    }

    // Built once per selection rather than per digest, so ng-repeat sees stable objects.

    function selectEntity() {
      const current = figures();
      vm.view = { figures: current, boxes: boxes(current), ageing: ageing(current) };
    }

    function figures() {
      if (!vm.summary) return null;
      if (vm.selectedCode === allEntities) return vm.summary.total;

      const entity = vm.summary.entities.find((x) => x.billingAccountCode === vm.selectedCode);
      return entity ? entity.figures : vm.summary.total;
    }

    // AC3: the box set is decided once, by the corporate figures; only the values rescope.

    function boxes(current) {
      const result = [
        {
          labelKey: 'StatementOfAccountTotalOutstanding',
          value: current.totalOutstanding,
          isLead: true,
        },
      ];
      if (vm.summary.showRefundedAmount)
        result.push({ labelKey: 'StatementOfAccountBilledAmount', value: current.billedAmount });
      result.push({ labelKey: 'StatementOfAccountPaymentMade', value: current.paymentMade });
      if (vm.summary.showRefundedAmount)
        result.push({
          labelKey: 'StatementOfAccountRefundedAmount',
          value: current.refundedAmount,
        });
      if (vm.summary.showExcessPayment)
        result.push({ labelKey: 'StatementOfAccountExcessPayment', value: current.excessPayment });

      return result;
    }

    // The bar is weighted by amount. A bucket in credit draws nothing but still prints its amount.

    function ageing(current) {
      const positiveTotal = ageingBuckets.reduce((sum, x) => sum + Math.max(current[x.key], 0), 0);

      return ageingBuckets.map((x) => ({
        labelKey: x.labelKey,
        color: x.color,
        amount: current[x.key],
        width: positiveTotal > 0 ? `${(Math.max(current[x.key], 0) / positiveTotal) * 100}%` : '0%',
      }));
    }

    // "MYR 12,504.71"; an account in credit prints as a minus figure, as the file does.

    function formatMoney(value) {
      const amount = $filter('number')(value, 2);
      return vm.summary && vm.summary.currencyCode
        ? `${vm.summary.currencyCode} ${amount}`
        : amount;
    }

    function canDownload() {
      return (
        !vm.isLoadingContext && !vm.isRestricted && !vm.isPreparing && !!vm.context && !!vm.summary
      );
    }

    function download() {
      if (!canDownload()) return;

      vm.isPreparing = true;
      vm.alert = null;

      statementSvc
        .outputStatementToExcel({ abpHandleError: false })
        .success((data) => {
          App.downloadTempFile(data);

          const entities = joinNames(data.entityNames || []);
          const date = formatAlertDate(data.asAtDate);
          let message = App.localize('StatementOfAccountDownloaded', entities, date);
          if (!data.hasOutstandingSheet) {
            message += ` ${App.localize('StatementOfAccountNothingOutstanding')}`;
          }

          vm.alert = { type: 'success', message };
        })
        .error((error) => {
          if (error && error.code === restrictedErrorCode) {
            vm.isRestricted = true;
            return;
          }

          vm.alert = {
            type: 'danger',
            title: App.localize('StatementOfAccountDownloadFailedTitle'),
            message: getErrorMessage(error),
          };
        })
        .finally(() => {
          vm.isPreparing = false;
        });
    }

    function headingDate() {
      return vm.context ? moment(vm.context.asAtDate, 'YYYY-MM-DD').format('D MMMM YYYY') : '';
    }

    function formatAlertDate(isoDate) {
      return moment(isoDate, 'YYYY-MM-DD').format('D MMM YYYY');
    }

    // The failure alert says what to do next and gives a reference to quote (HM-11295 AC5).

    function getErrorMessage(error) {
      if (error && error.code === notAvailableErrorCode) return error.message;

      const tenancyName = vm.context ? vm.context.tenancyName : appSession.tenant.tenancyName;
      const date = vm.context
        ? formatAlertDate(vm.context.asAtDate)
        : moment().format('D MMM YYYY');
      return App.localize('StatementOfAccountFailedMessage', `${tenancyName} · ${date}`);
    }

    // "A", "A and B", "A, B and C".

    function joinNames(names) {
      if (names.length <= 1) return names.join('');

      const and = App.localize('StatementOfAccountListAnd');
      return `${names.slice(0, -1).join(', ')} ${and} ${names[names.length - 1]}`;
    }
  }
})();
